import { bootstrapApplication } from '@angular/platform-browser';
import { RouteReuseStrategy, provideRouter, withPreloading, PreloadAllModules } from '@angular/router';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { provideHttpClient, withInterceptors, HttpClient } from '@angular/common/http';
import { importProvidersFrom, Injectable } from '@angular/core';
import { TranslateModule, TranslateLoader, MissingTranslationHandler, MissingTranslationHandlerParams } from '@ngx-translate/core';
import { FormlyModule, FormlyFieldConfig } from '@ngx-formly/core';
import { FormlyIonicModule } from '@ngx-formly/ionic';


import { routes } from '@app/app.routes';
import { AppComponent } from '@app/app.component';
import { authInterceptor } from '@app/core/interceptors/auth.interceptor';
import { customTranslateLoaderFactory } from '@app/core/loaders/custom-translate-loader';
import { FormlyFieldBooleanComponent } from '@app/shared/components/formly-components/formly-field-boolean/formly-field-boolean.component';
import { FormlyFieldCurrencyComponent } from '@app/shared/components/formly-components/formly-field-currency/formly-field-currency.component';
import { FormlyFieldDateTimeComponent } from '@app/shared/components/formly-components/formly-field-date-time/formly-field-date-time.component';
import { FormlyFieldHtmlComponent } from '@app/shared/components/formly-components/formly-field-html/formly-field-html.component';
import { FormlyFieldNumberComponent } from '@app/shared/components/formly-components/formly-field-number/formly-field-number.component';
import { FormlyFieldPictureComponent } from '@app/shared/components/formly-components/formly-field-picture/formly-field-picture.component';
import { FormlyFieldSelectComponent } from '@app/shared/components/formly-components/formly-field-select/formly-field-select.component';
import { FormlyFieldTextComponent } from '@app/shared/components/formly-components/formly-field-text/formly-field-text.component';
import { FormlyFieldLocationComponent } from '@app/shared/components/formly-components/formly-field-location/formly-field-location.component';
import { FormlyFieldAccordionWrapperComponent } from '@app/shared/components/formly-components/formly-field-accordion-wrapper/formly-field-accordion-wrapper.component';
import { FormlyFieldPageWrapperComponent } from '@app/shared/components/formly-components/formly-field-page/formly-field-page.component';
import { FormlyFieldWrapperComponent } from '@app/shared/components/formly-components/formly-field-wrapper/formly-field-wrapper.component';
import { QuillModule } from 'ngx-quill';
import { FormlyTreeBuilderComponent } from '@app/shared/components/formly-components/formly-field-tree-builder/formly-field-tree-builder.component';
import { FormlyTreeLevelComponent } from '@app/shared/components/formly-components/formly-field-tree-level/formly-field-tree-level.component';
import { FormlyFieldTreeSelectComponent } from '@app/shared/components/formly-components/formly-field-tree-select/formly-field-tree-select.component';
import { FormlyFieldCardWrapperComponent } from '@app/shared/components/formly-components/formly-field-card-wrapper/formly-field-card-wrapper.component';
import { FormlyFieldCheckboxComponent } from '@app/shared/components/formly-components/formly-field-checkbox/formly-field-checkbox.component';
import { register as registerSwiperElements } from 'swiper/element/bundle';
import { FormlyFieldSignatureComponent } from '@app/shared/components/formly-components/formly-field-signature/formly-field-signature.component';
import { FormlyFieldPagesWrapperComponent } from '@app/shared/components/formly-components/formly-field-pages/formly-field-pages.component';
import { FormlyFieldRepeatComponent } from '@app/shared/components/formly-components/formly-field-repeat/formly-field-repeat.component';
import { FormlyFieldDocumentsComponent } from '@app/shared/components/formly-components/formly-field-documents/formly-field-documents.component';
import { FormlyFieldClientSelectComponent } from '@app/shared/components/formly-components/formly-field-client-select/formly-field-client-select.component';
import { FormlyFieldEmailComponent } from '@app/shared/components/formly-components/formly-field-email/formly-field-email.component';
import { FormlyFieldTranslationComponent } from '@app/shared/components/formly-components/formly-field-translation/formly-field-translation.component';
import { FormlyFieldPermissionsComponent } from '@app/shared/components/formly-components/formly-field-permissions/formly-field-permissions.component';
import { FormlyFieldPrerequisiteRolesComponent } from '@app/shared/components/formly-components/formly-field-prerequisite-roles/formly-field-prerequisite-roles.component';
import { FormlyFieldUserRoleAssignmentsComponent } from '@app/shared/components/formly-components/formly-field-user-role-assignments/formly-field-user-role-assignments.component';
import { emailValidator, passwordMatchValidator, requiredRepeatValidator } from '@app/shared/components/formly-components/formly-field-validators';

// Register Swiper custom elements globally
registerSwiperElements();

@Injectable({
  providedIn: 'root'
})
export class MyMissingTranslationHandler implements MissingTranslationHandler {
    handle(params: MissingTranslationHandlerParams) {
        return params.key;
    }
}

bootstrapApplication(AppComponent, {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular(),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideHttpClient(withInterceptors([authInterceptor])),
    importProvidersFrom(
      IonicModule.forRoot(),
      QuillModule.forRoot(),
      TranslateModule.forRoot({
        defaultLanguage: 'fr-FR',
        useDefaultLang: true,
        loader: {
          provide: TranslateLoader,
          useFactory: customTranslateLoaderFactory,
          deps: [HttpClient],
        },
      }),
      FormlyModule.forRoot({
        types: [
          { name: 'BOOLEAN', component: FormlyFieldBooleanComponent, wrappers: ['BASIC_FIELD'] },
          { name: 'CHECKBOX', component: FormlyFieldCheckboxComponent, wrappers: ['BASIC_FIELD'] },
          { name: 'CLIENT_SELECT', component: FormlyFieldClientSelectComponent, wrappers: ['BASIC_FIELD'] },
          { name: 'CURRENCY', component: FormlyFieldCurrencyComponent, wrappers: ['BASIC_FIELD'] },
          { name: 'DATETIME', component: FormlyFieldDateTimeComponent, wrappers: ['BASIC_FIELD'] },
          { name: 'DOCUMENTS', component: FormlyFieldDocumentsComponent, wrappers: ['BASIC_FIELD'] },
          { name: 'HTML', component: FormlyFieldHtmlComponent, wrappers: ['BASIC_FIELD'] },
          { name: 'LOCATION', component: FormlyFieldLocationComponent, wrappers: ['BASIC_FIELD'] },
          { name: 'NUMBER', component: FormlyFieldNumberComponent, wrappers: ['BASIC_FIELD'] },
          { name: 'PERMISSIONS', component: FormlyFieldPermissionsComponent, wrappers: ['BASIC_FIELD'], defaultOptions: { defaultValue: [] } },
          { name: 'PREREQUISITE_ROLES', component: FormlyFieldPrerequisiteRolesComponent, wrappers: ['BASIC_FIELD'], defaultOptions: { defaultValue: [] } },
          { name: 'PICTURE', component: FormlyFieldPictureComponent, wrappers: ['BASIC_FIELD'] },
          { name: 'SELECT', component: FormlyFieldSelectComponent, wrappers: ['BASIC_FIELD'] },
          { name: 'SIGNATURE', component: FormlyFieldSignatureComponent, wrappers: ['BASIC_FIELD'] },
          { name: 'TEXT', component: FormlyFieldTextComponent, wrappers: ['BASIC_FIELD'] },
          { name: 'TEXTAREA', component: FormlyFieldTextComponent, wrappers: ['BASIC_FIELD'] },
          { name: 'TRANSLATION', component: FormlyFieldTranslationComponent, wrappers: ['BASIC_FIELD'], defaultOptions: { defaultValue: {} } },
          { name: 'USER_ROLE_ASSIGNMENTS', component: FormlyFieldUserRoleAssignmentsComponent, wrappers: ['BASIC_FIELD'], defaultOptions: { defaultValue: [] } },
          { name: 'TREE_BUILDER', component: FormlyTreeBuilderComponent, wrappers: ['BASIC_FIELD'] },
          { name: 'TREE_LEVEL',component: FormlyTreeLevelComponent, wrappers: ['BASIC_FIELD'] },
          { name: 'TREE_SELECT', component: FormlyFieldTreeSelectComponent, wrappers: ['BASIC_FIELD'], defaultOptions: { defaultValue: [] } },
          
          { name: 'REPEAT', component: FormlyFieldRepeatComponent, wrappers: ['BASIC_FIELD'], defaultOptions: {defaultValue: [] }},
          { name: 'EMAIL', component: FormlyFieldEmailComponent, wrappers: ['BASIC_FIELD'], defaultOptions: { validators: { validation: ['email'] } } },
        ], 
        wrappers: [
          { name: 'ACCORDION', component: FormlyFieldAccordionWrapperComponent },
          { name: 'PAGE', component: FormlyFieldPageWrapperComponent },
          { name: 'PAGES', component: FormlyFieldPagesWrapperComponent },
          { name: 'CARD', component: FormlyFieldCardWrapperComponent },
          { name: 'BASIC_FIELD', component: FormlyFieldWrapperComponent },
        ],

        validators: [
          { name: 'email', validation: emailValidator },
          { name: 'passwordMatch', validation: passwordMatchValidator },
          { name: 'requiredRepeat', validation: requiredRepeatValidator },          
        ],

        validationMessages: [
          { name: 'required', message: 'This field is required' },
          { name: 'minLength', message: 'Minimum length not met' },
          { name: 'maxLength', message: 'Maximum length exceeded' },
          { name: 'passwordMatch', message: 'Passwords must match' },
          { name: 'min', message: (error, field: FormlyFieldConfig) => `Value should be at least ${field.props?.min}` },
          { name: 'max', message: (error, field: FormlyFieldConfig) => `Value should be at most ${field.props?.max}` },
          { name: 'requiredRepeat', message: 'At least one entry is required' },
          { name: 'email', message: (error, field: FormlyFieldConfig) => `Please enter a valid email address. ${field.props?.['multiple'] ? 'If you want to enter multiple email addresses, separate them with a comma, semicolon, or newline.' : ''}` },
        ],
      }),
      FormlyIonicModule,
    ),
  ],
});
